import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

interface HappySnapModerationProps {
  eventId: string;
  onRefresh: () => void;
}

const HappySnapModeration: React.FC<HappySnapModerationProps> = ({ eventId, onRefresh }) => {
  const [photos, setPhotos] = useState<any[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => { setToast({ message, type }); setTimeout(() => setToast(null), 4000); };

  const loadPhotos = async () => {
    setLoading(true);
    try {
      let all: any[] = [];
      let nextToken: string | null = null;
      do {
        const result: any = await (client.models as any).CompetitionPhoto.list({ filter: { eventId: { eq: eventId } }, ...(nextToken ? { nextToken } : {}) });
        all = all.concat(result.data || []);
        nextToken = result.nextToken ?? null;
      } while (nextToken);
      setPhotos(all);

      const urls: Record<string, string> = {};
      await Promise.allSettled(all.map(async (p: any) => {
        try {
          const r = await (client.queries as any).generateDownloadUrl({ s3Key: p.photoS3Key });
          const d = parseLambdaResponse(r?.data);
          if (d?.downloadUrl) urls[p.id] = d.downloadUrl;
        } catch { /* skip */ }
      }));
      setPhotoUrls(urls);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadPhotos(); }, [eventId]);

  const handleAction = async (photoId: string, action: string) => {
    setActionLoading(photoId);
    try {
      const result = await (client.mutations as any).moderateHappySnap({ photoId, action });
      const data = parseLambdaResponse(result?.data);
      if (!data?.success) throw new Error(data?.error || 'Action failed');
      showToast(`Photo ${action === 'APPROVE' ? 'approved' : action === 'REJECT' ? 'rejected' : 'marked as winner'}`, 'success');
      await loadPhotos();
      onRefresh();
    } catch (err: any) {
      showToast(err.message || 'Failed', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (photoId: string) => {
    if (!window.confirm('Delete this photo?')) return;
    setActionLoading(photoId);
    try {
      const result = await (client.mutations as any).deleteHappySnap({ photoId });
      const data = parseLambdaResponse(result?.data);
      if (!data?.success) throw new Error(data?.error || 'Delete failed');
      showToast('Photo deleted', 'success');
      await loadPhotos();
      onRefresh();
    } catch (err: any) {
      showToast(err.message || 'Failed to delete', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = filter === 'ALL' ? photos : photos.filter((p: any) => p.status === filter);
  const counts = { PENDING: photos.filter((p: any) => p.status === 'PENDING').length, APPROVED: photos.filter((p: any) => p.status === 'APPROVED').length, REJECTED: photos.filter((p: any) => p.status === 'REJECTED').length, WINNER: photos.filter((p: any) => p.status === 'WINNER').length };

  if (loading) return <div className="text-center py-8 text-gray-500">Loading happy snaps…</div>;

  return (
    <div>
      {toast && <div role="alert" aria-live="assertive" className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>{toast.message}</div>}

      <div className="flex flex-wrap gap-2 mb-4">
        {['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'WINNER'].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === s ? 'bg-fishing-blue text-white' : 'bg-gray-100 text-gray-700'}`}>
            {s} {s !== 'ALL' ? `(${counts[s as keyof typeof counts]})` : `(${photos.length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No photos to review.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p: any) => (
            <div key={p.id} className={`bg-white rounded-lg shadow-sm overflow-hidden ${p.status === 'WINNER' ? 'ring-4 ring-yellow-400' : 'border border-gray-200'}`}>
              {photoUrls[p.id] ? <img src={photoUrls[p.id]} alt={p.caption || 'Happy snap'} className="w-full h-40 object-cover" /> : <div className="w-full h-40 bg-gray-100 flex items-center justify-center text-3xl">📷</div>}
              <div className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">{p.memberName}</span>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${p.status === 'APPROVED' ? 'bg-green-100 text-green-800' : p.status === 'REJECTED' ? 'bg-red-100 text-red-800' : p.status === 'WINNER' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>{p.status}</span>
                </div>
                {p.caption && <div className="text-xs text-gray-500 mb-2">{p.caption}</div>}
                <div className="flex gap-1 flex-wrap">
                  {p.status === 'PENDING' && <>
                    <button onClick={() => handleAction(p.id, 'APPROVE')} disabled={actionLoading === p.id} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300">Approve</button>
                    <button onClick={() => handleAction(p.id, 'REJECT')} disabled={actionLoading === p.id} className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300">Reject</button>
                  </>}
                  {(p.status === 'APPROVED') && <button onClick={() => handleAction(p.id, 'MARK_WINNER')} disabled={actionLoading === p.id} className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-gray-300">🏆 Mark Winner</button>}
                  <button onClick={() => handleDelete(p.id)} disabled={actionLoading === p.id} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:bg-gray-100">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HappySnapModeration;
